import React, { useState, useEffect } from "react";
import * as Styled from "./styled";
import Sidemenu from "../../components/sidemenu";
import { functionService } from "../../lib/functions";
import { Select, DatePicker, Dropdown, Menu, Button, Drawer, Input, Tooltip, Modal, Upload, Table } from "antd";
import { message } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Papa from "papaparse";

const { RangePicker } = DatePicker;
const { Dragger } = Upload;

const USE_MOCK_DATA = false;


const truncateText = (text, maxLength = 25) => {
  if (!text) return "-";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

/* ─────────────────────────────────────────────
   Shared pagination styles
───────────────────────────────────────────── */
const paginationStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 4px",
    borderTop: "1px solid #F0F0F0",
    marginTop: "8px",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#6B7280",
  },
  pageControls: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pageBtn: (active) => ({
    minWidth: "34px",
    height: "34px",
    padding: "0 10px",
    borderRadius: "8px",
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)" : "#FFFFFF",
    color: active ? "#FFFFFF" : "#374151",
    fontWeight: active ? 600 : 400,
    fontSize: "13px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: active ? "0 2px 10px rgba(79,70,229,0.3)" : "none",
  }),
  ellipsis: {
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9CA3AF",
    fontSize: "14px",
    userSelect: "none",
  },
  navBtn: (disabled) => ({
    height: "34px",
    padding: "0 14px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    background: disabled ? "#F9FAFB" : "#FFFFFF",
    color: disabled ? "#D1D5DB" : "#374151",
    fontWeight: 500,
    fontSize: "13px",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    transition: "all 0.15s ease",
  }),
};

/* ─────────────────────────────────────────────
   Bulk upload illustration — inline SVG, no
   external image asset required
───────────────────────────────────────────── */
const BulkUploadIllustration = () => (
  <svg
    width="180"
    height="140"
    viewBox="0 0 180 140"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="45" cy="30" r="16" fill="#EDE9FE" />
    <circle cx="140" cy="25" r="10" fill="#DBEAFE" />
    <circle cx="150" cy="60" r="7" fill="#FDE68A" />

    <rect x="35" y="35" width="90" height="62" rx="6" fill="#FFFFFF" stroke="#C7D2FE" strokeWidth="2" />
    <rect x="43" y="43" width="74" height="40" rx="3" fill="#EEF2FF" />
    <rect x="66" y="101" width="28" height="6" rx="2" fill="#C7D2FE" />
    <rect x="58" y="107" width="44" height="4" rx="2" fill="#E0E7FF" />

    <path
      d="M80 74V54M80 54L71 63M80 54L89 63"
      stroke="#4F46E5"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <rect x="118" y="70" width="30" height="38" rx="4" fill="#FFFFFF" stroke="#FBCFE8" strokeWidth="2" />
    <rect x="124" y="78" width="18" height="3" rx="1.5" fill="#F9A8D4" />
    <rect x="124" y="85" width="18" height="3" rx="1.5" fill="#FBCFE8" />
    <rect x="124" y="92" width="12" height="3" rx="1.5" fill="#FBCFE8" />

    <circle cx="30" cy="95" r="8" fill="#FCA5A5" />
    <path d="M18 128C18 116 24 110 30 110C36 110 42 116 42 128" fill="#FDBA74" />
    <rect x="12" y="120" width="12" height="18" rx="4" fill="#4F46E5" transform="rotate(-15 12 120)" />
  </svg>
);

const DemandDashboard = () => {
  const [demandListings, setDemandListings] = useState([]);
  const [filteredDemandListings, setFilteredDemandListings] = useState([]);

  const [accountList, setAccountList] = useState([]);
  const [subAccountOptions, setSubAccountOptions] = useState([]);
  const [endClientOptions, setEndClientOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [selectedEndClient, setSelectedEndClient] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState([]);
  const [jcodeSearch, setJcodeSearch] = useState("");
  const [receivedDateRange, setReceivedDateRange] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);

  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Editable fields on the drawer — names match demand_dashboard columns
  const [editAccountName, setEditAccountName] = useState("");
  const [editSubAccount, setEditSubAccount] = useState("");
  const [editEndClient, setEditEndClient] = useState("");
  const [editRoleName, setEditRoleName] = useState("");
  const [editCoffeeeeRemarks, setEditCoffeeeeRemarks] = useState("");
  const [editExperience, setEditExperience] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editBudget, setEditBudget] = useState("");
  const [editOpenPositions, setEditOpenPositions] = useState("");
  const [editRoleStatus, setEditRoleStatus] = useState("");
  const [editMandatorySkills, setEditMandatorySkills] = useState("");
  const [editModeOfWork, setEditModeOfWork] = useState("");
  const [editPriority, setEditPriority] = useState(null);
  const [editJcode, setEditJcode] = useState("");
  const [editReceivedDate, setEditReceivedDate] = useState(null);

  // ── Bulk upload modal state ──
  const [isBulkUploadModalVisible, setIsBulkUploadModalVisible] = useState(false);
  const [bulkUploadFileList, setBulkUploadFileList] = useState([]);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadErrors, setBulkUploadErrors] = useState([]); // [{row, column, message}]
  const [roleStatusOptions, setRoleStatusOptions] = useState([]);
  const [selectedRoleStatus, setSelectedRoleStatus] = useState();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);


  /* ── Data fetching ───────────────────────── */
  const fetchDemandListings = async () => {

    try {
      const res = await functionService.getDemandDashboardListings();
      if (res?.status && res?.response?.data?.data) {
        setDemandListings(res.response.data.data);
      } else {
        console.error("Error fetching demand dashboard listings:", res?.error);
        message.error("Failed to load demand dashboard data.");
      }
    } catch (error) {
      console.error("Error in fetchDemandListings:", error);
      message.error("An error occurred while loading data.");
    }
  };

  useEffect(() => {
    fetchDemandListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Derived filter options ──────────────── */
  useEffect(() => {
    setAccountList(
      [...new Set(demandListings.map((item) => item.account_name).filter(Boolean))].sort()
    );

    const base = selectedAccount
      ? demandListings.filter((item) => item.account_name === selectedAccount)
      : demandListings;

    setSubAccountOptions(
      [...new Set(base.map((item) => item.sub_account).filter(Boolean))].sort()
    );
    setEndClientOptions(
      [...new Set(base.map((item) => item.end_client).filter(Boolean))].sort()
    );
    setRoleOptions(
      [...new Set(base.map((item) => item.role_name).filter(Boolean))].sort()
    );
    setRoleStatusOptions([
      "Open",
      "On Hold",
      "Closed",
    ]);
    setPriorityOptions(
      [...new Set(demandListings.map((item) => item.priority).filter(Boolean))].sort()
    );
  }, [selectedAccount, demandListings]);

  useEffect(() => {
    setSelectedSubAccount(null);
    setSelectedEndClient(null);
    setSelectedRole(null);
    setSelectedRoleStatus(null);
  }, [selectedAccount]);

  /* ── Filtering ────────────────────────────── */
  useEffect(() => {
    let filteredListings = demandListings;

    if (selectedAccount)
      filteredListings = filteredListings.filter((item) => item.account_name === selectedAccount);
    if (selectedSubAccount)
      filteredListings = filteredListings.filter((item) => item.sub_account === selectedSubAccount);
    if (selectedEndClient)
      filteredListings = filteredListings.filter((item) => item.end_client === selectedEndClient);
    if (selectedRole)
      filteredListings = filteredListings.filter((item) => item.role_name === selectedRole);
    if (selectedPriority.length > 0) {
      filteredListings = filteredListings.filter((item) =>
        selectedPriority.includes(item.priority)
      );
    }
    if (selectedRoleStatus)
      filteredListings = filteredListings.filter(
        (item) =>
          item.role_status?.trim().toLowerCase() ===
          selectedRoleStatus.toLowerCase()
      );
    if (jcodeSearch) {
      const search = jcodeSearch.toLowerCase();
      filteredListings = filteredListings.filter((item) =>
        item.jcode?.toLowerCase().includes(search)
      );
    }

    if (receivedDateRange && receivedDateRange.length === 2) {
      const [fromDate, toDate] = receivedDateRange;
      const from = fromDate.startOf("day").valueOf();
      const to = toDate.endOf("day").valueOf();

      filteredListings = filteredListings.filter((item) => {
        if (!item.received_date) return false;
        const itemDate = new Date(item.received_date).getTime();
        if (isNaN(itemDate)) return false;
        return itemDate >= from && itemDate <= to;
      });
    }

    setFilteredDemandListings(filteredListings);
    setCurrentPage(1);
  }, [
    demandListings, selectedAccount, selectedSubAccount, selectedEndClient,
    selectedRole, selectedRoleStatus, selectedPriority, jcodeSearch, receivedDateRange
  ]);

  const totalPages = Math.ceil(filteredDemandListings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedListings = filteredDemandListings.slice(startIndex, startIndex + itemsPerPage);
  const showEmptyState = filteredDemandListings.length === 0;

  /* ── Summary card metrics (UI-only, derived from filtered data) ── */
  const summary = React.useMemo(() => {
    const total = filteredDemandListings.length;
    const onHoldCount = filteredDemandListings.filter(
      (i) => (i.role_status || "").trim().toLowerCase() === "on hold"
    ).length;
    const closedCount = filteredDemandListings.filter(
      (i) => (i.role_status || "").trim().toLowerCase() === "closed"
    ).length;
    const totalOpenings = filteredDemandListings.reduce(
      (acc, i) => acc + (Number(i.open_positions) || 0),
      0
    );
    const activeProfiles = filteredDemandListings.reduce(
      (acc, i) => acc + (Number(i.active_profiles) || 0),
      0
    );
    return { total, onHoldCount, closedCount, totalOpenings, activeProfiles };
  }, [filteredDemandListings]);

  /* ── Pagination page numbers builder ─────────── */
  const buildPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return "Invalid date";
    }
  };

  /* ── Row selection ───────────────────────── */
  const handleRowSelect = (row) => {
    setSelectedRows((prev) =>
      prev.includes(row) ? prev.filter((r) => r !== row) : [...prev, row]
    );
  };

  const handleSelectAll = (isChecked) => {
    setSelectedRows(isChecked ? paginatedListings : []);
  };

  /* ── Edit drawer ──────────────────────────── */
  const openEditDrawer = (item) => {
    setEditingRecord(item);
    setEditAccountName(item.account_name ?? "");
    setEditSubAccount(item.sub_account ?? "");
    setEditEndClient(item.end_client ?? "");
    setEditRoleName(item.role_name ?? "");
    setEditCoffeeeeRemarks(item.coffeeee_remarks ?? "");
    setEditExperience(item.experience ?? "");
    setEditLocation(item.location ?? "");
    setEditBudget(item.budget ?? "");
    setEditOpenPositions(item.open_positions ?? "");
    setEditRoleStatus(item.role_status ?? "");
    setEditMandatorySkills(item.mandatory_skills ?? "");
    setEditModeOfWork(item.mode_of_work ?? "");
    setEditPriority(item.priority ?? null);
    setEditJcode(item.jcode ?? "");
    setEditReceivedDate(item.received_date ? dayjs(item.received_date) : null);
    setIsEditDrawerVisible(true);
  };

  const closeEditDrawer = () => {
    setIsEditDrawerVisible(false);
    setEditingRecord(null);
  };

  const handleEditSubmit = async () => {
    if (!editingRecord?.id) return;

    setEditLoading(true);
    try {
      // Payload keys match demand_dashboard columns exactly — this is
      // what DemandModel.updateDemandDashboard() destructures.
      const payload = {
        account_name: editAccountName,
        sub_account: editSubAccount,
        end_client: editEndClient,
        role_name: editRoleName,
        jcode: editJcode,
        priority: editPriority,
        open_positions: editOpenPositions === "" ? null : editOpenPositions,
        experience: editExperience,
        budget: editBudget,
        location: editLocation,
        role_status: editRoleStatus,
        coffeeee_remarks: editCoffeeeeRemarks,
        mandatory_skills: editMandatorySkills,
        mode_of_work: editModeOfWork,
        received_date: editReceivedDate ? editReceivedDate.format("YYYY-MM-DD") : null,
      };

      if (USE_MOCK_DATA) {
        setDemandListings((prev) =>
          prev.map((row) => (row.id === editingRecord.id ? { ...row, ...payload } : row))
        );
        message.success("Demand updated successfully (mock data)");
        closeEditDrawer();
        return;
      }

      const res = await functionService.updateDemandDashboard(editingRecord.id, payload);

      if (res?.status) {
        closeEditDrawer();

        await fetchDemandListings();

        message.success("Demand updated successfully");
      } else {
        console.error("Update failed:", res?.error);
        message.error("A demand with the same Account Name, Sub Account, End Client and Role Name already exists.");
      }
    } catch (error) {
      console.error("Error updating demand:", error);
      message.error("An error occurred. Please try again.");
    } finally {
      setEditLoading(false);
    }
  };

  /* ── Bulk upload ──────────────────────────── */
  const openBulkUploadModal = () => {
    setBulkUploadFileList([]);
    setIsBulkUploadModalVisible(true);
    setBulkUploadErrors([]);
  };

  const closeBulkUploadModal = () => {
    setIsBulkUploadModalVisible(false);
    setBulkUploadFileList([]);
    // setBulkUploadErrors([]);
  };

  const handleDownloadSample = () => {
    const headers = [
      "Account Name",
      "Sub Account",
      "End Client",
      "Role Name",
      "Jcode",
      "Priority",
      "No of Openings",
      "Experience",
      "Budget",
      "Location",
      "Role Status",
      "Coffeeee Remarks",
      "Mandatory Skills",
      "Mode of Work",
      "Received On",
    ];

    const sampleRow = [
      "TCS",
      "FIL",
      "Kushagra",
      "Software Developer",
      "JC-1001",
      "P0",
      "5",
      "6-9 Years",
      "40 LPA",
      "Lucknow / Remote",
      "Open",
      "Urgent Requirement",
      "React, Node.js, PostgreSQL",
      "Hybrid",
      "12-Jun-2026",
    ];

    const csvContent = [
      headers.join(","),
      sampleRow
        .map((value) => `"${value}"`) // prevents comma issues in values
        .join(","),
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "demand_bulk_upload_sample.csv";

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  };

  // Required CSV headers for bulk upload — must match exactly
  const REQUIRED_CSV_HEADERS = [
    "Account Name",
    "Sub Account",
    "End Client",
    "Role Name",
    "No of Openings",
    "Coffeeee Remarks",
    "Experience",
    "Location",
    "Budget",
    "Role Status",
    "Jcode",
    "Priority",
    "Mandatory Skills",
    "Mode of Work",
    "Received On",
  ];

  const normalizeHeader = (header) => {
    return header
      ?.trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  };


  /* ── Bulk upload helpers ──────────────────────
     Pure functions used by handleBulkUploadSubmit:
     - validate that all required headers are present
     - convert a single CSV row into the backend payload shape
     - validate a single row and return an error string (or null if ok)
  ──────────────────────────────────────────────── */
  const validateCsvHeaders = (parsedFields) => {

    const errors = [];

    const normalizedRequiredHeaders =
      REQUIRED_CSV_HEADERS.map(normalizeHeader);


    const normalizedUploadedHeaders =
      parsedFields.map(normalizeHeader);


    // Missing columns
    REQUIRED_CSV_HEADERS.forEach((header) => {

      if (
        !normalizedUploadedHeaders.includes(
          normalizeHeader(header)
        )
      ) {
        errors.push({
          row: "-",
          column: "Header",
          message: `Missing column: ${header}`,
        });
      }

    });


    // Extra columns
    parsedFields.forEach((header) => {

      if (
        !normalizedRequiredHeaders.includes(
          normalizeHeader(header)
        )
      ) {

        errors.push({
          row: "-",
          column: "Header",
          message: `Extra column: ${header}`,
        });

      }

    });


    return errors;

  };

  const mapCsvRowToPayload = (row) => {

    const normalizedRow =
      normalizeCsvRowHeaders(row);


    const getValue = (key) => {

      return normalizedRow[
        normalizeHeader(key)
      ];

    };


    const toNumberOrNull = (val) => {

      if (
        val === undefined ||
        val === null ||
        String(val).trim() === ""
      ) {
        return null;
      }

      return Number(val);

    };


    return {

      account_name:
        getValue("Account Name")?.trim() || null,


      sub_account:
        getValue("Sub Account")?.trim() || null,


      end_client:
        getValue("End Client")?.trim() || null,


      role_name:
        getValue("Role Name")?.trim() || null,


      open_positions:
        toNumberOrNull(
          getValue("No of Openings")
        ),


      coffeeee_remarks:
        getValue("Coffeeee Remarks")?.trim() || null,


      experience:
        getValue("Experience")?.trim() || null,


      location:
        getValue("Location")?.trim() || null,


      budget:
        getValue("Budget")?.trim() || null,


      role_status:
        getValue("Role Status")?.trim() || null,


      jcode:
        getValue("Jcode")?.trim() || null,


      priority:
        getValue("Priority")?.trim() || null,

      mandatory_skills:
        getValue("Mandatory Skills")?.trim() || null,


      mode_of_work:
        getValue("Mode of Work")?.trim() || null,

      received_date:
        getValue("Received On")?.trim() || null,

    };

  };

  // Returns an error message string for the given (already-mapped) row, or null if valid
  const validateBulkUploadRow = (payloadRow) => {
    const errors = [];

    // Validate No of Openings only if provided
    if (
      payloadRow.open_positions !== null &&
      Number.isNaN(payloadRow.open_positions)
    ) {
      errors.push({
        column: "No of Openings",
        message: "No of Openings must be a valid number.",
      });
    }

    // Validate Priority
    if (payloadRow.priority) {
      const priority = String(payloadRow.priority).trim().toUpperCase();

      if (!["P0", "P1", "P2", "P3"].includes(priority)) {
        errors.push({
          column: "Priority",
          message: "Priority must be one of: P0, P1, P2 or P3.",
        });
      }
    }

    // Validate Role Status
    if (payloadRow.role_status) {
      const roleStatus = String(payloadRow.role_status).trim().toLowerCase();

      if (!["open", "on hold", "closed"].includes(roleStatus)) {
        errors.push({
          column: "Role Status",
          message: "Role Status must be one of: Open, On Hold or Closed.",
        });
      }
    }

    if (payloadRow.received_date) {
      const parsed = dayjs(
        String(payloadRow.received_date).trim(),
        [
          "D-MMM-YY",
          "DD-MMM-YY",
          "D-MMM-YYYY",
          "DD-MMM-YYYY",
          "D MMM YY",
          "DD MMM YY",
          "D MMM YYYY",
          "DD MMM YYYY",
          "DD-MM-YYYY",
          "D-MM-YYYY",
          "DD/MM/YYYY",
          "D/MM/YYYY",
          "MM/DD/YYYY",
          "YYYY-MM-DD",
        ],
        true
      );

      if (!parsed.isValid()) {
        errors.push({
          column: "Received On",
          message: "Received On is not a valid date.",
        });
      }
    }

    return errors; // [] if the row is fully valid
  };

  const validateDuplicateRows = (payloadRows) => {
    const errors = [];
    const seen = new Map();

    payloadRows.forEach((row, index) => {
      const key = [
        row.account_name,
        row.sub_account,
        row.end_client,
        row.role_name,
      ]
        .map((v) => (v || "").trim().toLowerCase())
        .join("|");

      if (!key.replace(/\|/g, "")) return;

      if (seen.has(key)) {
        errors.push({
          row: index + 2,
          column: "Duplicate",
          message: `Duplicate of Row ${seen.get(key)} (same Account Name + Sub Account + End Client + Role Name).`,
        });
      } else {
        seen.set(key, index + 2);
      }
    });

    return errors;
  };
  const normalizeCsvRowHeaders = (row) => {

    const normalizedRow = {};

    Object.keys(row).forEach((key) => {

      const normalizedKey =
        key
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ");

      normalizedRow[normalizedKey] = row[key];

    });


    return normalizedRow;

  };

  const handleBulkUploadSubmit = () => {
    if (!bulkUploadFileList.length) {
      message.error("Please select a CSV file.");
      return;
    }

    const file = bulkUploadFileList[0];
    setBulkUploadErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,

      complete: async (results) => {
        const { data, meta, errors: parseErrors } = results;

        if (parseErrors.length) {
          const mappedParseErrors = parseErrors.map((err) => ({
            row: err.row !== undefined ? err.row + 2 : "-",
            column: "CSV",
            message: err.message,
          }));

          setBulkUploadErrors(mappedParseErrors);
          message.error(parseErrors[0].message);
          return;
        }

        if (!data.length) {
          message.error("CSV contains no data.");
          return;
        }

        const headerErrors = validateCsvHeaders(meta.fields);


        if (headerErrors.length) {

          setBulkUploadErrors(headerErrors);

          message.error(
            `${headerErrors.length} header error(s) found. File was not uploaded.`
          );

          return;

        }
        const payload = [];
        const validationErrors = [];

        // Validate every row and collect ALL errors — no early exit per row
        for (let i = 0; i < data.length; i++) {
          const mappedRow = mapCsvRowToPayload(data[i]);
          const rowErrors = validateBulkUploadRow(mappedRow);

          if (rowErrors.length) {
            rowErrors.forEach((err) => {
              validationErrors.push({
                row: i + 2,
                column: err.column,
                message: err.message,
              });
            });
          }

          // still build payload row so it's ready to go if validation passes overall
          payload.push(mappedRow);
        }

        const duplicateErrors = validateDuplicateRows(payload);

        validationErrors.push(...duplicateErrors);

        // ── New all-or-nothing gate ──
        if (validationErrors.length > 0) {
          setBulkUploadErrors(validationErrors);
          message.error(
            `${validationErrors.length} error(s) found. Fix them and re-upload — no rows were uploaded.`
          );
          return; // do NOT call the backend
        }

        // Only reached when validationErrors.length === 0
        try {
          setIsBulkUploading(true);

          const res = await functionService.bulkUploadDemandDashboard({
            rows: payload,
          });

          console.log("Bulk Upload Response:", res);

          if (res.status) {

            await fetchDemandListings();

            const inserted = res.response.data.data.inserted;
            const duplicates = res.response.data.data.duplicates;

            if (duplicates.length > 0) {

              setBulkUploadErrors(duplicates);
              setShowDuplicateModal(true);

              message.success(
                `${inserted} row(s) uploaded successfully. ${duplicates.length} duplicate row(s) skipped.`
              );

            } else {

              setBulkUploadErrors([]);
              message.success(`${inserted} row(s) uploaded successfully.`);

            }

            closeBulkUploadModal();

          } else {

            message.error(
              res.error?.response?.data?.message || "Upload Failed"
            );

          }
        } catch (err) {
          message.error("Upload Failed");
        } finally {
          setIsBulkUploading(false);
        }
      },

      error: function () {
        setBulkUploadErrors([
          { row: "-", column: "CSV", message: "Unable to parse CSV." },
        ]);
        message.error("Unable to parse CSV.");
      },
    });
  };

  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;


  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    fileList: bulkUploadFileList,

    beforeUpload: (file) => {
      const fileSizeMB = file.size / (1024 * 1024);


      // File size validation
      if (file.size > MAX_FILE_SIZE_BYTES) {
        message.error(
          `"${file.name}" is ${fileSizeMB.toFixed(1)} MB — maximum allowed is ${MAX_FILE_SIZE_MB} MB.`
        );

        return Upload.LIST_IGNORE;
      }

      setBulkUploadFileList([file]);

      return false;
    },

    onRemove: () => {
      setBulkUploadFileList([]);
    },
  };

  /* ── Export ───────────────────────────────── */
  const exportToCSV = () => {
    const baseData = selectedRows.length > 0 ? selectedRows : filteredDemandListings;

    const headers = [
      "Account Name",
      "Sub Account",
      "End Client",
      "Role Name",
      "Total Positions",
      "No of Openings",
      "Profiles Sent",
      "Active Profiles",
      "To be Shared",
      "Coffeee Remarks",
      "Experience",
      "Location",
      "Budget",
      "Mandatory Skills",
      "Mode of Work",
      "Role Status",
      "Jcode",
      "Priority",
      "Received On",
    ];

    const escape = (val) => {
      const str = val === null || val === undefined ? "-" : String(val);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const csvRows = [headers.join(",")];

    baseData.forEach((item) => {
      const row = [
        escape(item.account_name || "-"),
        escape(item.sub_account || "-"),
        escape(item.end_client || "-"),
        escape(item.role_name || "-"),
        escape(item.total_positions ?? "-"),
        escape(item.open_positions ?? "-"),
        escape(item.profiles_sent ?? "-"),
        escape(item.active_profiles ?? "-"),
        escape(item.to_be_shared ?? "-"),
        escape(item.coffeeee_remarks || "-"),
        escape(item.experience || "-"),
        escape(item.location || "-"),
        escape(item.budget || "-"),
        escape(item.mandatory_skills || "-"),
        escape(item.mode_of_work || "-"),
        escape(item.role_status || "-"),
        escape(item.jcode || "-"),
        escape(item.priority || "-"),
        escape(item.received_date ? formatDate(item.received_date) : "-"),
      ];
      csvRows.push(row.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `demand_dashboard_${new Date().toISOString().split("T")[0]}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setSelectedRows([]);
  };

  return (
    <Styled.MainDiv>
      <Sidemenu />
      <Styled.ContentWrapDiv>

        {/* ── Top bar ── */}
        <Styled.TopContainer>
          <div className="leftSide">Demand Dashboard</div>

          <div className="rightSide">
            <Styled.OutlineButton onClick={openBulkUploadModal}>
              Bulk Upload
            </Styled.OutlineButton>
            <Styled.GradientButton onClick={exportToCSV}>
              Export
            </Styled.GradientButton>
          </div>
        </Styled.TopContainer>

        {/* ── Summary cards ── */}
        <Styled.SummaryGrid>
          <Styled.SummaryCard color="#4F46E5" idx={0}>
            <Styled.SummaryValue color="#4F46E5">{summary.total}</Styled.SummaryValue>
            <Styled.SummaryLabel>Total Demands</Styled.SummaryLabel>
          </Styled.SummaryCard>
          <Styled.SummaryCard color="#10B981" idx={1}>
            <Styled.SummaryValue color="#10B981">{summary.activeProfiles}</Styled.SummaryValue>
            <Styled.SummaryLabel>Active Profile</Styled.SummaryLabel>
          </Styled.SummaryCard>
          <Styled.SummaryCard color="#F59E0B" idx={2}>
            <Styled.SummaryValue color="#F59E0B">
              {summary.onHoldCount + summary.closedCount}
            </Styled.SummaryValue>
            <Styled.SummaryLabel>On Hold / Closed</Styled.SummaryLabel>
          </Styled.SummaryCard>
          <Styled.SummaryCard color="#8B5CF6" idx={3}>
            <Styled.SummaryValue color="#8B5CF6">{summary.totalOpenings}</Styled.SummaryValue>
            <Styled.SummaryLabel>No of Openings</Styled.SummaryLabel>
          </Styled.SummaryCard>
        </Styled.SummaryGrid>

        {/* ── Filters ── */}
        <Styled.FilterRow>
          <Styled.StyledSelectWrap>
            <Select showSearch placeholder="Account Name" onChange={(value) => setSelectedAccount(value)} allowClear style={{ minWidth: "180px" }} value={selectedAccount}>
              {accountList.map((name) => (
                <Select.Option key={name} value={name}>{name}</Select.Option>
              ))}
            </Select>
          </Styled.StyledSelectWrap>

          <Styled.StyledSelectWrap>
            <Select showSearch placeholder="Sub Account" onChange={(value) => setSelectedSubAccount(value)} allowClear style={{ minWidth: "180px" }} value={selectedSubAccount}>
              {subAccountOptions.map((s) => (
                <Select.Option key={s} value={s}>{s}</Select.Option>
              ))}
            </Select>
          </Styled.StyledSelectWrap>

          <Styled.StyledSelectWrap>
            <Select showSearch placeholder="End Client" onChange={(value) => setSelectedEndClient(value)} allowClear style={{ minWidth: "180px" }} value={selectedEndClient}>
              {endClientOptions.map((e) => (
                <Select.Option key={e} value={e}>{e}</Select.Option>
              ))}
            </Select>
          </Styled.StyledSelectWrap>

          <Styled.StyledSelectWrap>
            <Select
              showSearch
              placeholder="Role Status"
              onChange={(value) => setSelectedRoleStatus(value)}
              allowClear
              style={{ minWidth: "180px" }}
              value={selectedRoleStatus}
            >
              {roleStatusOptions.map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Styled.StyledSelectWrap>

          <Styled.StyledSelectWrap>
            <Select
              showSearch
              placeholder="Role Name"
              onChange={(value) => setSelectedRole(value)}
              allowClear
              style={{ minWidth: "180px" }}
              value={selectedRole}
            >
              {roleOptions.map((role) => (
                <Select.Option key={role} value={role}>
                  <Tooltip title={role} placement="top">
                    <span style={{
                      display: "inline-block",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      verticalAlign: "middle"
                    }}>
                      {role}
                    </span>
                  </Tooltip>
                </Select.Option>
              ))}
            </Select>
          </Styled.StyledSelectWrap>

          <Styled.StyledSelectWrap>
            <Select
              mode="multiple"
              showSearch
              placeholder="Priority"
              onChange={setSelectedPriority}
              allowClear
              style={{ minWidth: "180px", flexShrink: 0 }}
              value={selectedPriority}
              maxTagCount="responsive"
            >
              {priorityOptions.map((p) => (
                <Select.Option key={p} value={p}>
                  {p}
                </Select.Option>
              ))}
            </Select>
          </Styled.StyledSelectWrap>

          <Styled.StyledInputWrap>
            <Input
              placeholder="Search by Jcode"
              value={jcodeSearch}
              onChange={(e) => setJcodeSearch(e.target.value)}
              allowClear
            />
          </Styled.StyledInputWrap>

          <Styled.StyledRangePickerWrap>
            <RangePicker
              value={receivedDateRange}
              onChange={(dates) => setReceivedDateRange(dates || null)}
              format="DD MMM YYYY"
              placeholder={["Received From", "Received To"]}
              separator={
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "24px",
                    minWidth: "24px",
                    flexShrink: 0,
                    color: "#6B7280",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "1",
                  }}
                >
                  →
                </span>
              }
              allowClear
              style={{
                width: "262px",
                minWidth: "262px",
              }}
            />
          </Styled.StyledRangePickerWrap>
        </Styled.FilterRow>

        <Styled.TableWrap style={{ overflowX: "auto", overflowY: "auto", maxHeight: "100%" }}>
          {showEmptyState ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "90%",
                marginTop: "30px",
              }}
            >
              <img
                src={require("../../images/Frame.png")}
                alt="No Data"
                style={{ width: "30%", height: "auto" }}
              />
              <h2 style={{ marginTop: "auto" }}>No Result Found</h2>
            </div>
          ) : (
            <Styled.MainTable
              style={{
                tableLayout: "fixed",   // was "fixed"
                width: "100%",
                minWidth: "100%",
              }}
              className="demand-dashboard-table"
            >
              <style>{`
                .demand-dashboard-table th,
                .demand-dashboard-table td {
                  padding: 6px 10px !important;
                }
                .demand-dashboard-table th {
                  white-space: normal;
                  line-height: 1.3;
                  vertical-align: middle;
                  height: auto !important;
                  min-height: 52px;
                }
              `}</style>
              <colgroup>
                <col style={{ width: "40px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "200px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "160px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "90px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "80px" }} />
              </colgroup>
              <thead style={{ position: "sticky", top: 0, zIndex: 20, background: "#F8F9FF" }}>
                <tr>
                  <th style={{ position: "sticky", left: 0, top: 0, zIndex: 11, background: "#F8F9FF", minWidth: "40px" }}>
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={selectedRows.length === paginatedListings.length && paginatedListings.length > 0}
                    />
                  </th>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 11,
                      background: "#F8F9FF",
                    }}
                  >
                    Account Name
                  </th>

                  <th
                    style={{
                      position: "sticky",
                      left: "50px",
                      zIndex: 11,
                      background: "#F8F9FF",
                    }}
                  >
                    Sub Account
                  </th>

                  <th
                    style={{
                      position: "sticky",
                      left: "100px",
                      zIndex: 11,
                      background: "#F8F9FF",
                    }}
                  >
                    End Client
                  </th>

                  <th
                    style={{
                      position: "sticky",
                      left: "150px",
                      zIndex: 11,
                      background: "#F8F9FF",
                    }}
                  >
                    Role Name
                  </th>
                  <th>No of Time Sourced</th>
                  <th>No of Openings</th>
                  <th>Profile Sourced</th>
                  <th>Profiles Sent</th>
                  <th>Active Profiles</th>
                  <th>Last worked on</th>
                  <th>To be Shared</th>
                  <th>Coffeee Remarks</th>
                  <th>Experience</th>
                  <th>Location</th>
                  <th>Budget</th>
                  <th>Mandatory Skills</th>
                  <th>Mode of Work</th>
                  <th>Role Status</th>
                  <th>Jcode</th>
                  <th>Priority</th>
                  <th>Received On</th>
                  <th style={{ background: "#F8F9FF" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedListings.map((item, index) => {
                  const rowBg = selectedRows.includes(item) ? "#EEF2FF" : "#ffffff";
                  const accountColor = Styled.getAccountColor(item.account_name);
                  return (
                    <tr key={item.id ?? index} style={{ backgroundColor: rowBg, transition: "background-color 0.15s ease-in-out" }}>
                      <td>
                        <input type="checkbox" onChange={() => handleRowSelect(item)} checked={selectedRows.includes(item)} />
                      </td>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 10,
                          background: rowBg,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={item.account_name || "-"} placement="topLeft">
                          <span style={{ display: "inline-block", maxWidth: "100%", cursor: "pointer" }}>
                            {item.account_name ? (
                              <Styled.AccountChip bg={accountColor.bg} fg={accountColor.fg}>
                                {item.account_name}
                              </Styled.AccountChip>
                            ) : "-"}
                          </span>
                        </Tooltip>
                      </td>

                      <td
                        style={{
                          position: "sticky",
                          left: "50px",
                          zIndex: 10,
                          background: rowBg,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={item.sub_account || "-"} placement="topLeft">
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {item.sub_account || "-"}
                          </span>
                        </Tooltip>
                      </td>

                      <td
                        style={{
                          position: "sticky",
                          left: "100px",
                          zIndex: 10,
                          background: rowBg,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={item.end_client || "-"} placement="topLeft">
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {item.end_client || "-"}
                          </span>
                        </Tooltip>
                      </td>

                      <td
                        style={{
                          position: "sticky",
                          left: "150px",
                          zIndex: 10,
                          background: rowBg,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={item.role_name || "-"} placement="topLeft">
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            {item.role_name || "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td>{item.time_count ?? "-"}</td>
                      <td>
                        <Styled.NumPill bg="#EEF2FF" fg="#4338CA">{item.open_positions ?? "-"}</Styled.NumPill>
                      </td>
                      <td>{item.total_positions ?? "-"}</td>
                      <td>
                        <Styled.NumPill bg="#FEF3C7" fg="#92400E">{item.profiles_sent ?? "-"}</Styled.NumPill>
                      </td>
                      <td>
                        <Styled.NumPill bg="#D1FAE5" fg="#065F46">{item.active_profiles ?? "-"}</Styled.NumPill>
                      </td>
                      <td>
                        <Styled.NumPill bg="#DBEAFE" fg="#1E40AF" style={{ minWidth: "87px" }}>
                          {item.last_worked_on
                            ? dayjs(item.last_worked_on).format("DD-MMM-YYYY")
                            : "-"}
                        </Styled.NumPill>
                      </td>
                      <td>
                        <Styled.NumPill bg="#FCE7F3" fg="#9D174D">{item.to_be_shared ?? "-"}</Styled.NumPill>
                      </td>
                      <td>
                        <Tooltip title={item.coffeeee_remarks || "-"} placement="topLeft">
                          <span style={{ maxWidth: "100%", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.coffeeee_remarks, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Tooltip title={item.experience || "-"} placement="topLeft">
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {item.experience || "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Tooltip title={item.location || "-"} placement="topLeft">
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {item.location || "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Tooltip title={item.budget || "-"} placement="topLeft">
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", fontWeight: 600, color: "#059669" }}>
                            {item.budget || "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={item.mandatory_skills || "-"} placement="topLeft">
                          <span style={{ maxWidth: "100%", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.mandatory_skills, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Tooltip title={item.mode_of_work || "-"} placement="topLeft">
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {item.mode_of_work || "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.role_status ? (
                          <Styled.StatusPill s={item.role_status}>{item.role_status}</Styled.StatusPill>
                        ) : "-"}
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: "110px" }}>
                        <Tooltip title={item.jcode || "-"} placement="topLeft">
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", fontFamily: "monospace", fontWeight: 600, color: "#4338CA" }}>
                            {item.jcode || "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.priority ? (
                          <Styled.PriorityPill p={item.priority}>{item.priority}</Styled.PriorityPill>
                        ) : "-"}
                      </td>
                      <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <Tooltip title={item.created_at ? formatDate(item.created_at) : "-"} placement="topLeft">
                          <span style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {item.created_at ? formatDate(item.created_at) : "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Styled.IconDiv>
                          <Tooltip title="Edit">
                            <Styled.OptionButton onClick={() => openEditDrawer(item)} />
                          </Tooltip>
                        </Styled.IconDiv>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Styled.MainTable>
          )}
        </Styled.TableWrap>

        {/* ── Pagination ── */}
        {!showEmptyState && (
          <div style={paginationStyles.container}>
            <div style={paginationStyles.leftSection}>
              <span>Show</span>
              <Styled.PaginationSelectWrapper>
                <Select
                  defaultValue={10}
                  size="small"
                  dropdownMatchSelectWidth={false}
                  onChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                >
                  <Select.Option value={10}>10</Select.Option>
                  <Select.Option value={20}>20</Select.Option>
                  <Select.Option value={50}>50</Select.Option>
                </Select>
              </Styled.PaginationSelectWrapper>
              <span>per page</span>
              <span style={{ marginLeft: "12px", color: "#9CA3AF" }}>|</span>
              <span style={{ marginLeft: "12px" }}>
                {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(currentPage * itemsPerPage, filteredDemandListings.length)} of{" "}
                {filteredDemandListings.length} results
              </span>
            </div>
            <div style={paginationStyles.pageControls}>
              <button
                style={paginationStyles.navBtn(currentPage === 1)}
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
              >
                ← Prev
              </button>
              {buildPageNumbers().map((page, idx) =>
                page === "..." ? (
                  <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>…</span>
                ) : (
                  <button
                    key={page}
                    style={paginationStyles.pageBtn(page === currentPage)}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                style={paginationStyles.navBtn(currentPage === totalPages)}
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── Edit Demand Drawer ── */}
        <Drawer
          title={
            <div>
              <div style={{ fontWeight: 700, fontSize: "16px" }}>Edit Demand</div>
              {editingRecord && (
                <div style={{ fontWeight: 400, fontSize: "13px", color: "#888", marginTop: "2px" }}>
                  {editingRecord.account_name} &nbsp;·&nbsp; {editingRecord.role_name}
                </div>
              )}
            </div>
          }
          placement="right"
          width={420}
          onClose={closeEditDrawer}
          visible={isEditDrawerVisible}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <Button onClick={closeEditDrawer}>Cancel</Button>
              <Button type="primary" loading={editLoading} onClick={handleEditSubmit}>
                Save Changes
              </Button>
            </div>
          }
        >
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Account Name</label>
            <Input
              value={editAccountName}
              onChange={(e) => setEditAccountName(e.target.value.toUpperCase())}
              style={{ textTransform: "uppercase" }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Sub Account</label>
            <Input value={editSubAccount} onChange={(e) => setEditSubAccount(e.target.value.toUpperCase())} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>End Client</label>
            <Input value={editEndClient} onChange={(e) => setEditEndClient(e.target.value.toUpperCase())} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Role Name</label>
            <Input
              value={editRoleName}
              onChange={(e) => setEditRoleName(e.target.value.toUpperCase())}
              style={{ textTransform: "uppercase" }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>No of Openings</label>
            <Input
              type="number"
              min={0}
              value={editOpenPositions}
              onChange={(e) => setEditOpenPositions(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Coffeee Remarks</label>
            <Input.TextArea value={editCoffeeeeRemarks} onChange={(e) => setEditCoffeeeeRemarks(e.target.value)} rows={3} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Experience</label>
            <Input value={editExperience} onChange={(e) => setEditExperience(e.target.value)} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Location</label>
            <Input value={editLocation} onChange={(e) => setEditLocation(e.target.value)} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Budget</label>
            <Input value={editBudget} onChange={(e) => setEditBudget(e.target.value)} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Mandatory Skills</label>
            <Input.TextArea value={editMandatorySkills} onChange={(e) => setEditMandatorySkills(e.target.value)} rows={2} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Mode of Work</label>
            <Input value={editModeOfWork} onChange={(e) => setEditModeOfWork(e.target.value)} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Role Status</label>
            <Input value={editRoleStatus} onChange={(e) => setEditRoleStatus(e.target.value)} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Jcode</label>
            <Input value={editJcode} onChange={(e) => setEditJcode(e.target.value)} />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Priority</label>
            <Input value={editPriority ?? ""} onChange={(e) => setEditPriority(e.target.value)} />
          </div>

          {/* <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#333" }}>Received On</label>
            <DatePicker
              value={editReceivedDate}
              onChange={(date) => setEditReceivedDate(date || null)}
              format="DD MMM YYYY"
              allowClear
              style={{ width: "100%" }}
            />
          </div> */}
        </Drawer>

        <Modal
          open={showDuplicateModal}
          title="Duplicate Records Skipped"
          onCancel={() => {
            setShowDuplicateModal(false);
            setBulkUploadErrors([]);
          }}
          width={900}
          style={{
            marginRight: 170, // Half of the sidebar width (240 / 2)
          }}
          footer={[
            <Button
              key="close"
              type="primary"
              onClick={() => setShowDuplicateModal(false)}
            >
              Close
            </Button>
          ]}
        // onCancel={() => setShowDuplicateModal(false)}
        >
          <Table
            bordered
            size="small"
            pagination={{
              pageSize: 10,
              hideOnSinglePage: true,
            }}
            dataSource={bulkUploadErrors.map((item, index) => ({
              key: index,
              ...item,
            }))}
            columns={[
              {
                title: "CSV Row",
                dataIndex: "row",
                width: 100,
              },
              {
                title: "Account Name",
                dataIndex: "account_name",
              },
              {
                title: "Sub Account",
                dataIndex: "sub_account",
              },
              {
                title: "End Client",
                dataIndex: "end_client",
              },
              {
                title: "Role Name",
                dataIndex: "role_name",
              },
              {
                title: "Reason",
                dataIndex: "message",
              },
            ]}
          />
        </Modal>

        {/* ── Bulk Upload Modal ── */}
        <Modal
          title={null}
          open={isBulkUploadModalVisible}
          onCancel={closeBulkUploadModal}
          footer={null}
          width={720}
          centered
          styles={{ body: { padding: 0 } }}
        >
          {/* Fixed Header */}
          <div style={{ padding: "20px 24px 12px 24px" }}>
            <div
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#111827",
                textAlign: "left",
              }}
            >
              Bulk Upload Demand
            </div>
          </div>

          <div style={{ borderBottom: "1px solid #F0F0F0" }} />


          {/* Scrollable Body */}
          <div
            style={{
              padding: "20px 24px",
              textAlign: "center",
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "12px",
              }}
            >
              <BulkUploadIllustration />
            </div>


            {bulkUploadFileList.length === 0 ? (
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#111827",
                  marginBottom: "20px",
                }}
              >
                No file uploaded so far
              </div>
            ) : (
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#166534",
                  marginBottom: "20px",
                }}
              >
                {bulkUploadFileList[0].name}
              </div>
            )}



            <Dragger
              {...uploadProps}
              showUploadList={false}
              style={{
                padding: "12px 0",
                marginBottom: "16px",
              }}
            >
              <Button
                type="primary"
                style={{
                  minWidth: "180px",
                }}
              >
                Upload Demand File
              </Button>

              <p
                style={{
                  fontSize: "12px",
                  color: "#9CA3AF",
                  marginTop: "10px",
                  marginBottom: 0,
                }}
              >
                or drag and drop your .csv file here
              </p>
            </Dragger>



            {/* Download Sample */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                fontSize: "13px",
                color: "#374151",
                marginBottom: "8px",
                whiteSpace: "nowrap",
              }}
            >
              <span>Download sample file:</span>

              <Button
                type="link"
                onClick={handleDownloadSample}
                style={{
                  padding: 0,
                  height: "auto",
                  fontSize: "13px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "1px",
                }}
              >
                Sample CSV (Demand Template)

                <DownloadOutlined
                  style={{
                    fontSize: "20px",
                    position: "relative",
                    top: "1px",
                  }}
                />

              </Button>
            </div>



            {/* Action Buttons */}
            {bulkUploadFileList.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                <Button onClick={closeBulkUploadModal}>
                  Cancel
                </Button>

                <Button
                  type="primary"
                  loading={isBulkUploading}
                  onClick={handleBulkUploadSubmit}
                >
                  Submit Upload
                </Button>
              </div>
            )}



            {/* Errors */}
            {bulkUploadErrors.length > 0 && (
              <div
                style={{
                  marginTop: "24px",
                  textAlign: "left",
                }}
              >

                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: "#B91C1C",
                    marginBottom: "8px",
                  }}
                >
                  {bulkUploadErrors.length} validation error
                  {bulkUploadErrors.length > 1 ? "s" : ""}
                  {" "}found — the file was not uploaded.
                </div>


                <div
                  style={{
                    maxHeight: "220px",
                    overflowY: "auto",
                    border: "1px solid #FECACA",
                    borderRadius: "8px",
                    background: "#FEF2F2",
                  }}
                >

                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >

                    <thead>
                      <tr
                        style={{
                          background: "#FEE2E2",
                          position: "sticky",
                          top: 0,
                        }}
                      >
                        <th style={{ padding: "8px 10px", textAlign: "left", width: "60px" }}>
                          Row
                        </th>

                        <th style={{ padding: "8px 10px", textAlign: "left", width: "140px" }}>
                          Column
                        </th>

                        <th style={{ padding: "8px 10px", textAlign: "left" }}>
                          Issue
                        </th>

                      </tr>
                    </thead>


                    <tbody>
                      {bulkUploadErrors.map((err, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderTop: "1px solid #FECACA",
                          }}
                        >

                          <td
                            style={{
                              padding: "6px 10px",
                              color: "#991B1B",
                              fontWeight: 600,
                            }}
                          >
                            {err.row}
                          </td>


                          <td
                            style={{
                              padding: "6px 10px",
                              color: "#991B1B",
                            }}
                          >
                            {err.column}
                          </td>


                          <td
                            style={{
                              padding: "6px 10px",
                              color: "#7F1D1D",
                            }}
                          >
                            {err.message}
                          </td>

                        </tr>
                      ))}
                    </tbody>

                  </table>

                </div>


                <div
                  style={{
                    fontSize: "12px",
                    color: "#6B7280",
                    marginTop: "8px",
                  }}
                >
                  Nothing has been uploaded yet. Fix every row listed above, then re-upload the full file.
                </div>


              </div>
            )}

          </div>

        </Modal>


      </Styled.ContentWrapDiv>
    </Styled.MainDiv>
  );
};

export default DemandDashboard;



